import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import SendIcon from '@mui/icons-material/Send';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import { useSnackbar } from 'notistack';

import {
  ActionMenu,
  ConfirmDialog,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
  ResponsiveTable,
} from '../components/common';
import type { Column } from '../components/common';
import announcementApi, {
  Announcement,
  AnnouncementMeta,
  AudienceType,
} from '../services/announcementApi';
import Gate from '../components/access/Gate';
import AnnouncementComposer from '../components/announcements/AnnouncementComposer';
import AnnouncementDetail from '../components/announcements/AnnouncementDetail';

const STATUS_LABELS: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'error' }> = {
  draft: { label: 'پیش‌نویس', color: 'default' },
  scheduled: { label: 'زمان‌بندی‌شده', color: 'info' },
  sending: { label: 'در حال ارسال', color: 'warning' },
  sent: { label: 'ارسال شد', color: 'success' },
  cancelled: { label: 'لغو شده', color: 'default' },
  failed: { label: 'ناموفق', color: 'error' },
};

const AUDIENCE_LABELS: Record<AudienceType, string> = {
  all: 'همهٔ کاربران',
  buyers: 'خریداران',
  sellers: 'فروشندگان',
  admins: 'مدیران',
  specific: 'کاربران مشخص',
  segment: 'بخش‌بندی سفارشی',
  rules: 'دستهٔ قاعده‌محور',
};

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('fa-IR');

const jalali = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const AnnouncementManagement: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [meta, setMeta] = useState<AnnouncementMeta | null>(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [detail, setDetail] = useState<Announcement | null>(null);

  const [confirm, setConfirm] = useState<{
    open: boolean;
    title: string;
    message: string;
    destructive?: boolean;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await announcementApi.list({
      page,
      limit: 20,
      status: statusFilter,
      search: search.trim() || undefined,
    });
    if (res.success && res.data) {
      setItems(res.data.docs || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalDocs(res.data.totalDocs || 0);
    } else {
      setError(res.message || 'خطا در دریافت فهرست اطلاعیه‌ها');
    }
    setLoading(false);
  }, [page, statusFilter, search]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      const res = await announcementApi.getMeta();
      if (res.success && res.data) setMeta(res.data);
    })();
  }, []);

  // A run in progress only updates its counters server-side, so poll while any
  // row is still sending instead of making the admin refresh by hand.
  useEffect(() => {
    if (!items.some((i) => i.status === 'sending')) return;
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [items, load]);

  const stats = useMemo(() => {
    const sent = items.filter((i) => i.status === 'sent');
    return {
      total: totalDocs,
      sending: items.filter((i) => i.status === 'sending').length,
      scheduled: items.filter((i) => i.status === 'scheduled').length,
      reached: sent.reduce((sum, i) => sum + (i.stats?.delivered ?? 0), 0),
    };
  }, [items, totalDocs]);

  const handleSend = (row: Announcement) => {
    setConfirm({
      open: true,
      title: 'ارسال اطلاعیه',
      message:
        `این اطلاعیه به مخاطبان «${AUDIENCE_LABELS[row.audience?.type ?? 'all']}» ارسال می‌شود ` +
        'و پس از شروع قابل بازگشت نیست. مطمئن هستید؟',
      onConfirm: async () => {
        const res = await announcementApi.send(row._id);
        enqueueSnackbar(res.message || (res.success ? 'ارسال آغاز شد' : 'خطا'), { variant: res.success ? 'success' : 'error' });
        setConfirm((c) => ({ ...c, open: false }));
        void load();
      },
    });
  };

  const handleCancel = (row: Announcement) => {
    setConfirm({
      open: true,
      title: 'لغو اطلاعیه',
      message: 'این اطلاعیه لغو می‌شود و در زمان تعیین‌شده ارسال نخواهد شد.',
      onConfirm: async () => {
        const res = await announcementApi.cancel(row._id);
        enqueueSnackbar(res.message || (res.success ? 'لغو شد' : 'خطا'), { variant: res.success ? 'success' : 'error' });
        setConfirm((c) => ({ ...c, open: false }));
        void load();
      },
    });
  };

  const handleDelete = (row: Announcement) => {
    setConfirm({
      open: true,
      title: 'حذف اطلاعیه',
      message: 'اطلاعیه برای همیشه حذف می‌شود. اعلان‌هایی که قبلاً ارسال شده‌اند حذف نمی‌شوند.',
      destructive: true,
      onConfirm: async () => {
        const res = await announcementApi.remove(row._id);
        enqueueSnackbar(res.message || (res.success ? 'حذف شد' : 'خطا'), { variant: res.success ? 'success' : 'error' });
        setConfirm((c) => ({ ...c, open: false }));
        void load();
      },
    });
  };

  const handleDuplicate = async (row: Announcement) => {
    const res = await announcementApi.duplicate(row._id);
    enqueueSnackbar(res.message || (res.success ? 'رونوشت ساخته شد' : 'خطا'), { variant: res.success ? 'success' : 'error' });
    if (res.success && res.data) {
      setEditing(res.data);
      setComposerOpen(true);
    }
    void load();
  };

  const columns: Column<Announcement>[] = [
    {
      id: 'title',
      label: 'اطلاعیه',
      priority: 1,
      format: (_v, row) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {row.message}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'audience',
      label: 'مخاطب',
      priority: 2,
      format: (_v, row) => (
        <Chip
          size="small"
          icon={<PeopleIcon sx={{ fontSize: 15 }} />}
          label={AUDIENCE_LABELS[row.audience?.type ?? 'all']}
          variant="outlined"
        />
      ),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 1,
      format: (_v, row) => {
        const s = STATUS_LABELS[row.status] || STATUS_LABELS.draft;
        return (
          <Stack spacing={0.5}>
            <Chip size="small" label={s.label} color={s.color} variant="filled" />
            {row.status === 'sending' && (
              <LinearProgress
                variant={row.stats?.targeted ? 'determinate' : 'indeterminate'}
                value={
                  row.stats?.targeted
                    ? Math.min(100, (row.stats.delivered / row.stats.targeted) * 100)
                    : undefined
                }
                sx={{ height: 4, borderRadius: 2 }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      id: 'stats',
      label: 'رسیده به',
      priority: 2,
      format: (_v, row) =>
        ['sent', 'sending'].includes(row.status) ? (
          <Stack spacing={0.25}>
            <Typography variant="body2" fontWeight={700}>
              {fmt(row.stats?.delivered)} / {fmt(row.stats?.targeted)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              پوش: {fmt(row.stats?.pushSent)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      id: 'scheduledAt',
      label: 'زمان',
      priority: 3,
      detail: true,
      format: (_v, row) =>
        row.status === 'scheduled' ? jalali(row.scheduledAt) : jalali(row.completedAt || row.createdAt),
    },
    {
      id: 'createdBy',
      label: 'سازنده',
      priority: 3,
      detail: true,
      format: (_v, row) =>
        typeof row.createdBy === 'object' ? row.createdBy?.name || '—' : '—',
    },
  ];

  const renderActions = (row: Announcement) => {
    const editable = ['draft', 'scheduled'].includes(row.status);
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {editable && (
          <Gate permission="admin_announcement_send">
            <Tooltip title="ارسال">
              <IconButton size="small" color="primary" onClick={() => handleSend(row)}>
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Gate>
        )}
        <ActionMenu
          items={[
            {
              label: 'جزئیات و آمار',
              icon: <CampaignIcon fontSize="small" />,
              onClick: () => setDetail(row),
            },
            {
              label: 'ویرایش',
              icon: <EditIcon fontSize="small" />,
              hidden: !editable,
              onClick: () => {
                setEditing(row);
                setComposerOpen(true);
              },
            },
            {
              label: 'رونوشت',
              icon: <ContentCopyIcon fontSize="small" />,
              onClick: () => void handleDuplicate(row),
            },
            {
              label: 'لغو',
              icon: <CancelIcon fontSize="small" />,
              hidden: row.status !== 'scheduled' && row.status !== 'draft',
              onClick: () => handleCancel(row),
              divider: true,
            },
            {
              label: 'حذف',
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              hidden: row.status === 'sending',
              onClick: () => handleDelete(row),
            },
          ]}
        />
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <PageHeader
        title="اطلاعیه‌های همگانی"
        subtitle="ارسال اطلاعیه و پوش‌نوتیفیکیشن به کاربران"
      >
        <Stack direction="row" spacing={1}>
            <Tooltip title="بارگذاری مجدد">
              <IconButton onClick={() => void load()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Gate permission="admin_announcement_create">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditing(null);
                  setComposerOpen(true);
                }}
              >
                اطلاعیه جدید
              </Button>
            </Gate>
        </Stack>
      </PageHeader>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'کل اطلاعیه‌ها', value: stats.total, color: theme.palette.primary.main },
          { label: 'در حال ارسال', value: stats.sending, color: theme.palette.warning.main },
          { label: 'زمان‌بندی‌شده', value: stats.scheduled, color: theme.palette.info.main },
          { label: 'اعلان تحویل‌شده', value: stats.reached, color: theme.palette.success.main },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, borderColor: 'divider' }}
            >
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>
                {fmt(s.value)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            fullWidth
            placeholder="جستجو در عنوان و متن…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>وضعیت</InputLabel>
            <Select
              label="وضعیت"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <MenuItem value="all">همه</MenuItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingSkeleton variant="table" />
      ) : items.length === 0 ? (
        <EmptyState
          variant={search || statusFilter !== 'all' ? 'no-results' : 'empty'}
          title={search || statusFilter !== 'all' ? 'نتیجه‌ای یافت نشد' : 'هنوز اطلاعیه‌ای نساخته‌اید'}
          description={
            search || statusFilter !== 'all'
              ? 'فیلترها را تغییر دهید'
              : 'با «اطلاعیه جدید» اولین پیام همگانی را بسازید'
          }
        />
      ) : (
        <>
          <ResponsiveTable
            columns={columns}
            data={items}
            keyField="_id"
            onRowClick={(row) => setDetail(row)}
            renderActions={renderActions}
          />
          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                قبلی
              </Button>
              <Typography variant="body2">
                صفحه {fmt(page)} از {fmt(totalPages)}
              </Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                بعدی
              </Button>
            </Stack>
          )}
        </>
      )}

      <AnnouncementComposer
        open={composerOpen}
        meta={meta}
        initial={editing}
        onClose={() => {
          setComposerOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          setComposerOpen(false);
          setEditing(null);
          void load();
        }}
      />

      <AnnouncementDetail
        announcement={detail}
        onClose={() => setDetail(null)}
        onChanged={() => void load()}
      />

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        destructive={confirm.destructive}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm((c) => ({ ...c, open: false }))}
      />
    </Box>
  );
};

export default AnnouncementManagement;
